import React, {useState, useRef, useEffect} from 'react';
import './Select.css';

export default function Select({
  id,
  name,
  value,
  onChange,
  options = [],
  optionsErrors = [],
  placeholder = 'Выберите...',
  isMulti = false,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const rootRef = useRef(null);

  // Normalize value
  const selectedValues = isMulti
    ? Array.isArray(value)
      ? value
      : []
    : value == null
    ? []
    : [value];

  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setOpen(false);
        setFocusIndex(-1);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function toggleOpen() {
    setOpen((s) => !s);
    if (!open) setFocusIndex(0);
  }

  function handleSelect(option) {
    if (isMulti) {
      const exists = selectedValues.includes(option.value);
      const next = exists
        ? selectedValues.filter((v) => v !== option.value)
        : [...selectedValues, option.value];
      onChange && onChange(next);
    } else {
      const isAlreadySelected = selectedValues[0] === option.value;
      onChange && onChange(isAlreadySelected ? null : option.value);
      setOpen(false);
    }
  }

  function handleRemoveTag(val) {
    if (!isMulti) return;
    const next = selectedValues.filter((v) => v !== val);
    onChange && onChange(next);
  }

  function onKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setFocusIndex(0);
        return;
      }
      const opt = options[focusIndex];
      if (opt) handleSelect(opt);
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setFocusIndex(-1);
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setFocusIndex((i) => Math.min(i + 1, options.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIndex((i) => Math.max(i - 1, 0));
    }
  }

  const selectedLabels = options
    .filter((o) => selectedValues.includes(o.value))
    .map((o) => ({value: o.value, label: o.label}));

  return (
    <div
      className={`custom-select ${className} ${open ? 'open' : ''}`}
      ref={rootRef}
      onKeyDown={onKeyDown}
      tabIndex={0}
      aria-expanded={open}
      aria-haspopup='listbox'
      aria-owns={`${id}-listbox`}
    >
      <input
        type='hidden'
        name={name}
        value={
          isMulti ? JSON.stringify(selectedValues) : selectedValues[0] ?? ''
        }
      />
      <button
        type='button'
        className='select-control'
        onClick={toggleOpen}
        aria-label='toggle select'
      >
        <div className='select-value'>
          {selectedLabels.length === 0 ? (
            <span className='placeholder'>{placeholder}</span>
          ) : isMulti ? (
            <div className='tags'>
              {selectedLabels.map((t) => (
                <span
                  key={t.value}
                  className={`${'tag'} ${
                    optionsErrors.includes(t.value)
                      ? 'custom-select__tag-err'
                      : ''
                  }`}
                >
                  {t.label}
                  <div
                    type='button'
                    className='tag-remove'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveTag(t.value);
                    }}
                    aria-label={`remove ${t.label}`}
                  >
                    ×
                  </div>
                </span>
              ))}
            </div>
          ) : (
            <span className='single-label'>{selectedLabels[0].label}</span>
          )}
        </div>
        <span className='arrow' />
      </button>

      {open && (
        <ul className='options' id={`${id}-listbox`} role='listbox'>
          {options.length === 0 && (
            <li className='option empty'>Нет вариантов</li>
          )}
          {options.map((opt, i) => {
            const isSelected = selectedValues.includes(opt.value);
            const isFocused = i === focusIndex;
            return (
              <li
                key={opt.value}
                role='option'
                aria-selected={isSelected}
                className={`option ${isSelected ? 'selected' : ''} ${
                  isFocused ? 'focused' : ''
                } `}
                onMouseEnter={() => setFocusIndex(i)}
                onClick={() => handleSelect(opt)}
              >
                <span className='label'>{opt.label}</span>
                {isSelected && <span className='check'>✓</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
